import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchMealPlan } from "../services/mealPlansService";
import {
  getPlan,
  hydratePlan,
  savePlan,
  subscribeToPlans,
} from "../services/plansService";
import type { PlanRecord } from "../types/plans.types";

/**
 * Loading, ready, no such plan, or the request failed.
 *
 * `missing` and `failed` are kept apart because they are different answers: a
 * plan that is not there is not coming back however many times it is asked
 * for, and a request that fell over is worth a Try again.
 */
export type PlanStatus = "loading" | "ready" | "missing" | "failed";

export interface PlanState {
  /** `undefined` until it has loaded, and for good if `status` is not ready. */
  plan: PlanRecord | undefined;
  status: PlanStatus;
  /** Set only when `status` is `failed` — what the screen shows. */
  error?: string;
  /** Writes an edited copy back. Local: there is no `PATCH` for a plan yet. */
  save: (plan: PlanRecord) => void;
  /** Runs the fetch again. For a Try again button. */
  reload: () => void;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

/**
 * One plan, by the `public_id` in the route — so a bookmarked builder, or a
 * refresh, finds its plan without the list screen having been visited.
 *
 * Fetched from `/api/v1/meal-plans/{id}/`, which is the only place a plan's
 * meals come from, then kept in `plansService` for the builder to edit. A
 * plan already there is not fetched again: it is either one created in this
 * sitting, one already being edited, or a seeded fixture — and in each case
 * the copy in hand is the newer one.
 *
 * The record's identity only changes when it is saved, which is what lets the
 * builder hold an editable copy of it in local state.
 */
export function usePlan(planId: string): PlanState {
  const getToken = useTokenGetter();

  const plan = useSyncExternalStore(
    subscribeToPlans,
    () => getPlan(planId),
    () => getPlan(planId)
  );

  // Starts ready when the store already has it, so opening a plan straight
  // after creating it does not flash a loading screen for a fetch it will
  // not make.
  const [status, setStatus] = useState<PlanStatus>(() =>
    planId && getPlan(planId) ? "ready" : "loading"
  );
  const [error, setError] = useState<string>();
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // A token that did not decode is "", which is no plan rather than a
    // request to make.
    if (!planId) {
      setStatus("missing");
      return;
    }

    // Read through the store rather than the value above, so a save — which
    // gives the record a new identity — does not re-run this and refetch.
    if (getPlan(planId)) {
      setStatus("ready");
      return;
    }

    const controller = new AbortController();
    setStatus("loading");
    setError(undefined);

    fetchMealPlan(getToken, planId, controller.signal)
      .then((record) => {
        if (controller.signal.aborted) return;
        hydratePlan(record);
        setStatus("ready");
      })
      .catch((reason) => {
        if (controller.signal.aborted) return;
        // A 404 is an answer, not a failure: the plan is not there, and
        // asking again would get the same reply.
        if (reason instanceof ApiError && reason.status === 404) {
          setStatus("missing");
          return;
        }
        console.error("Plan failed to load:", reason);
        setError(failureMessage(reason));
        setStatus("failed");
      });

    return () => controller.abort();
  }, [planId, getToken, attempt]);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  return { plan, status, error, save: savePlan, reload };
}
